/**
 * Visual Stage — Stage 4: Visual aid validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.5
 *
 * Validates:
 * - Required families have visual aids
 * - stimulusAids/supportAids follow semantic convention
 * - Visual types match policy-allowed types (multi-type support)
 * - Visual aid types are known (in VisualAidType union)
 * - Visual aid data matches schema (VISUAL_AID_SCHEMAS.json)
 * - No answer leakage in visual data
 */

import type { ValidationIssue } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import { getVisualPolicy, familyRequiresVisual } from '../policyEngine'
import type { Question } from '../../../content'
import { VISUAL_AID_TYPES } from '../../../visuals'
import visualAidSchemas from '../../data/VISUAL_AID_SCHEMAS.json'
import { SEVERITY } from '../severityScale'
import { STAGES } from '../stageDefinitions'
import { getModuleRulebook, deriveQuestionTypeExempt } from '../../rulebooks'
import { getQuestionTypeVisualRole } from '../../../content/questions/questionTypeRegistry'

// SSOT for stage numbers — see stageDefinitions.ts (G-16 fix, RC-3)
const STAGE = STAGES.VISUAL

/** Set of known visual aid types for O(1) lookup */
const KNOWN_VISUAL_TYPES = new Set<string>(VISUAL_AID_TYPES)

/**
 * Returns true when the question type's visual surface is provided by
 * its own renderer (visualRole === 'embedded').
 *
 * SSOT: `QUESTION_TYPE_VISUAL_ROLE` in questionTypeRegistry.ts. Any type
 * whose `visualRole === 'embedded'` is auto-exempt from rulebook stimulus
 * checks. Adding a new embedded question type to the registry extends
 * this set automatically — no parallel update needed here.
 *
 * @see docs/specs/VISUAL_INTERACTIVE_STRATEGY_2026.md §5.3
 */
function isQuestionTypeVisuallyEmbedded(type: string | undefined): boolean {
  if (!type) return false
  return getQuestionTypeVisualRole(type) === 'embedded'
}

/** Parsed schema definitions for data validation */
const SCHEMA_DEFS = (visualAidSchemas as Record<string, unknown>)
  .definitions as Record<string, Record<string, unknown>> | undefined

/**
 * Schema violation surfaced by {@link validateVisualDataSchema}.
 *
 * - `severity: 'blocker'` — the data is structurally wrong in a way that the
 *   renderer/resolver cannot recover from (missing required field, wrong
 *   primitive type, value outside min/max, array items of wrong type).
 *   Examples that motivated this: `barModel.segments = 121` (silently
 *   clamped to 20 → indicator off-screen), `barModel.segments = [{...}]`
 *   (resolver returns null → no visual renders),
 *   `barModel.highlighted = [{...}]` (BarModel iterates as Set → broken).
 * - `severity: 'coach'` — softer schema mismatch worth a review note but
 *   not blocking rendering. (Currently unused — kept for future schema
 *   extensions like enums.)
 */
type VisualSchemaViolation = {
  field: string
  message: string
  severity: 'blocker' | 'coach'
  suggestion?: string
}

/**
 * Schema validation for a visual aid's `data` payload.
 *
 * Reads `VISUAL_AID_SCHEMAS.json` and surfaces:
 *  - Missing `required` fields (coach — preserved historical behaviour).
 *  - Wrong primitive type for a declared `properties[field].type`
 *    (blocker — the resolver/component cannot work).
 *  - Numeric range violations (`minimum` / `maximum`) (blocker).
 *  - Array items with wrong `items.type` (blocker).
 *
 * Multi-type schema entries (e.g. `"type": ["number", "string"]`) are
 * accepted when any listed type matches the actual value.
 *
 * Why this matters: prior to this check, seeds like `GET-PROC-BTW`
 * (`segments: 121`), `GET-PROC-TOENAME` (`segments: [...]`) and
 * `GET-BREUK-DEEL` (`totalParts` instead of `total`) all passed
 * validation but rendered as broken / blank visuals. The pipeline now
 * catches these at the visual stage as blockers.
 */
function validateVisualDataSchema(
  type: string,
  data: Record<string, unknown>,
): VisualSchemaViolation[] {
  if (!SCHEMA_DEFS) return []
  const schema = SCHEMA_DEFS[type]
  if (!schema) return [] // No schema defined for this type

  const violations: VisualSchemaViolation[] = []

  // 1. Required-field presence (coach severity, historical behaviour)
  const required = schema.required as string[] | undefined
  if (required && required.length > 0) {
    for (const field of required) {
      if (
        !(field in data) ||
        data[field] === undefined ||
        data[field] === null
      ) {
        violations.push({
          field,
          message: `missing required field "${field}"`,
          severity: 'coach',
          suggestion: `Add "${field}" to the ${type} visual data object`,
        })
      }
    }
  }

  // 2. Per-property type / range / items checks (blocker severity)
  const properties = schema.properties as
    | Record<string, Record<string, unknown>>
    | undefined
  if (properties) {
    for (const [field, propSchema] of Object.entries(properties)) {
      if (!(field in data)) continue
      const value = data[field]
      if (value === undefined || value === null) continue

      // 2a. Primitive type check — `type` can be a string or string[] in JSON-schema.
      const declaredType = propSchema.type as string | string[] | undefined
      if (declaredType) {
        const allowed = Array.isArray(declaredType)
          ? declaredType
          : [declaredType]
        const actual = Array.isArray(value) ? 'array' : typeof value
        if (!allowed.includes(actual)) {
          violations.push({
            field,
            message: `field "${field}" must be of type ${allowed.join(' | ')}, got ${actual}`,
            severity: 'blocker',
            suggestion: `Pass a ${allowed.join(' or ')} for ${type}.${field}. ${
              actual === 'object'
                ? 'You may have nested the value inside an object — flatten it.'
                : ''
            }`.trim(),
          })
          // Skip further checks on a value that has the wrong type
          continue
        }
      }

      // 2b. Numeric range checks
      if (typeof value === 'number') {
        const min = propSchema.minimum as number | undefined
        const max = propSchema.maximum as number | undefined
        if (typeof min === 'number' && value < min) {
          violations.push({
            field,
            message: `field "${field}" = ${value} is below minimum ${min}`,
            severity: 'blocker',
            suggestion: `Use a value ≥ ${min} for ${type}.${field}`,
          })
        }
        if (typeof max === 'number' && value > max) {
          violations.push({
            field,
            message: `field "${field}" = ${value} exceeds maximum ${max}`,
            severity: 'blocker',
            suggestion:
              type === 'barModel' && field === 'segments'
                ? `barModel renders at most 20 segments. For ratios that need more (e.g. 100:21 BTW), use percentageBar or step_solver instead.`
                : `Use a value ≤ ${max} for ${type}.${field}`,
          })
        }
      }

      // 2c. Array item-type checks
      if (Array.isArray(value)) {
        const items = propSchema.items as Record<string, unknown> | undefined
        const itemType = items?.type as string | string[] | undefined
        if (itemType) {
          const allowedItem = Array.isArray(itemType) ? itemType : [itemType]
          for (let i = 0; i < value.length; i++) {
            const elem = value[i]
            const elemType = Array.isArray(elem) ? 'array' : typeof elem
            if (elem !== null && !allowedItem.includes(elemType)) {
              violations.push({
                field,
                message: `field "${field}[${i}]" must be ${allowedItem.join(' | ')}, got ${elemType}`,
                severity: 'blocker',
                suggestion: `${type}.${field} expects an array of ${allowedItem.join(' or ')} — drop wrapper objects/keys.`,
              })
              break // one report per array is enough
            }
          }
        }
      }
    }
  }

  return violations
}

/**
 * Returns true when the actual aid type is semantically equivalent to the
 * rulebook hint, accounting for renderer aliases.
 *
 * Currently handles: `fraction` (a generic FractionVisual that can render
 * as a bar strip when `data.visualType === "bar"`) is equivalent to
 * `fractionStrip` for rulebook-hint purposes. Both share the same didactic
 * intent (linear strip-based fraction reasoning).
 */
function hintEquivalent(
  hintType: string,
  aidType: string,
  aidData: Record<string, unknown> | undefined,
): boolean {
  if (hintType === aidType) return true
  // fraction (visualType: "bar") ≈ fractionStrip
  if (hintType === 'fractionStrip' && aidType === 'fraction') {
    const visualType = aidData?.visualType
    if (visualType === 'bar' || visualType === undefined) return true
  }
  if (hintType === 'fraction' && aidType === 'fractionStrip') return true
  return false
}

/**
 * Rulebook hint compliance — checks whether actual stimulus/support visual
 * types match the per-family `preferredStimulus` / `preferredSupport`
 * declared in the module rulebook's `visuals.familyVisualHints`.
 *
 * This is a non-blocking coach signal. It only fires when aids exist and
 * disagree with the rulebook — missing-visual cases are covered by
 * VISUAL_STIMULUS_REQUIRED / VISUAL_SUPPORT_RECOMMENDED elsewhere.
 *
 * Severity: LOW for stimulus, NEAR_LOW for support — a step above pure info.
 *
 * SHRINK (cleanup pass): we now skip emitting the rulebook-hint mismatch
 * when the aid type is ALSO outside the policy `stimulusAllowed` /
 * `supportAllowed` list. In that case VISUAL_STIMULUS_TYPE_MISMATCH or
 * VISUAL_SUPPORT_TYPE_MISMATCH already fires on the same aid — the
 * rulebook-hint signal would be pure duplication. The hint only adds
 * value when the aid is "policy-OK but rulebook prefers something else".
 */
function runRulebookHintCheck(
  question: Question,
  stimulusAllowed?: ReadonlySet<string>,
  supportAllowed?: ReadonlySet<string>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? ''
  if (!fId) return issues

  const rulebook = getModuleRulebook(question.module)
  if (!rulebook) return issues

  const hints = rulebook.visuals?.familyVisualHints
  if (!hints || hints.length === 0) return issues

  const stimulusAids = Array.isArray(question.stimulusAids)
    ? question.stimulusAids
    : []
  const supportAids = Array.isArray(question.supportAids)
    ? question.supportAids
    : []

  const qType = question.questionType ?? ''

  for (const hint of hints) {
    let matched = false
    try {
      matched = new RegExp(hint.familyIdPattern).test(fId)
    } catch {
      continue
    }
    if (!matched) continue

    // Honor questionTypeExempt — skip hint for exempt types.
    // SSOT-derived: deriveQuestionTypeExempt auto-extends with embedded
    // types when hint.autoExemptEmbedded is true (governance Fase 1).
    const exemptSet = deriveQuestionTypeExempt(hint)
    if (exemptSet.has(qType)) {
      // First-match-wins (juni 2026): even an exempt match consumes the
      // first-match slot, otherwise a more generic fallback pattern would
      // double-warn on the same family.
      break
    }

    // Stimulus check — only when aids exist
    if (hint.preferredStimulus && stimulusAids.length > 0) {
      for (let i = 0; i < stimulusAids.length; i++) {
        const aid = stimulusAids[i] as Record<string, unknown> | undefined
        const aidType =
          aid && typeof aid === 'object' && typeof aid.type === 'string'
            ? (aid.type as string)
            : undefined
        const aidData =
          aid && typeof aid === 'object'
            ? (aid.data as Record<string, unknown> | undefined)
            : undefined
        if (
          aidType &&
          !hintEquivalent(hint.preferredStimulus, aidType, aidData)
        ) {
          // SHRINK: skip when aid is also outside the policy allowed-list
          // — VISUAL_STIMULUS_TYPE_MISMATCH already fires on the same aid.
          if (stimulusAllowed && !stimulusAllowed.has(aidType)) continue
          // ALLOWED-LIST RESPECT (juni 2026): when the rulebook hint
          // explicitly lists this aid type in `stimulusAllowed`, the
          // author has approved it as a valid alternative. Don't nag.
          if (hint.stimulusAllowed && hint.stimulusAllowed.includes(aidType)) {
            continue
          }
          issues.push(
            coach(
              'visual',
              STAGE,
              'VISUAL_RULEBOOK_HINT_STIMULUS_MISMATCH',
              `Stimulus visual "${aidType}" does not match the rulebook hint "${hint.preferredStimulus}" for family pattern ${hint.familyIdPattern}`,
              {
                field: `stimulusAids[${i}].type`,
                expected: hint.preferredStimulus,
                actual: aidType,
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.LOW,
                suggestion: hint.note
                  ? `Rulebook: ${hint.note} — consider switching to "${hint.preferredStimulus}".`
                  : `Consider switching to "${hint.preferredStimulus}" per rulebook hint.`,
              },
            ),
          )
        }
      }
    }

    // Support check — only when aids exist
    if (hint.preferredSupport && supportAids.length > 0) {
      for (let i = 0; i < supportAids.length; i++) {
        const aid = supportAids[i] as Record<string, unknown> | undefined
        const aidType =
          aid && typeof aid === 'object' && typeof aid.type === 'string'
            ? (aid.type as string)
            : undefined
        const aidData =
          aid && typeof aid === 'object'
            ? (aid.data as Record<string, unknown> | undefined)
            : undefined
        if (
          aidType &&
          !hintEquivalent(hint.preferredSupport, aidType, aidData)
        ) {
          // SHRINK: skip when aid is also outside the policy allowed-list
          // — VISUAL_SUPPORT_TYPE_MISMATCH already fires on the same aid.
          if (supportAllowed && !supportAllowed.has(aidType)) continue
          // ALLOWED-LIST RESPECT (juni 2026): when the rulebook hint
          // explicitly lists this aid type in `supportAllowed`, the
          // author has approved it as a valid alternative. Don't nag.
          if (hint.supportAllowed && hint.supportAllowed.includes(aidType)) {
            continue
          }
          issues.push(
            coach(
              'visual',
              STAGE,
              'VISUAL_RULEBOOK_HINT_SUPPORT_MISMATCH',
              `Support visual "${aidType}" does not match the rulebook hint "${hint.preferredSupport}" for family pattern ${hint.familyIdPattern}`,
              {
                field: `supportAids[${i}].type`,
                expected: hint.preferredSupport,
                actual: aidType,
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.NEAR_LOW,
                suggestion: hint.note
                  ? `Rulebook: ${hint.note} — consider switching to "${hint.preferredSupport}".`
                  : `Consider switching to "${hint.preferredSupport}" per rulebook hint.`,
              },
            ),
          )
        }
      }
    }

    // FIRST-MATCH-WINS (juni 2026): the rulebook header documents
    // "patterns are evaluated TOP-DOWN; first match wins". The rule-only
    // check at line ~692 already implements this. Apply the same here so
    // a specific subfamily pattern (e.g. ^GET-VERH-SCHAAL-KAART)
    // overrides a more generic fallback (e.g. ^GET-VERH-SCHAAL) instead
    // of double-firing.
    break
  }

  return issues
}

/**
 * Run visual validation on a question.
 */
export function runVisualStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues

  const visualPolicy = getVisualPolicy(question.familyId)
  const isRequired = familyRequiresVisual(question.familyId)

  // ── Unknown visual type + data schema validation (all questions) ──────

  const allAids: Array<{
    aid: Record<string, unknown>
    role: string
    idx: number
  }> = []

  if (Array.isArray(question.stimulusAids)) {
    for (let i = 0; i < question.stimulusAids.length; i++) {
      const aid = question.stimulusAids[i] as
        | Record<string, unknown>
        | undefined
      if (aid && typeof aid === 'object') {
        allAids.push({ aid, role: 'stimulusAids', idx: i })
      }
    }
  }
  if (Array.isArray(question.supportAids)) {
    for (let i = 0; i < question.supportAids.length; i++) {
      const aid = question.supportAids[i] as Record<string, unknown> | undefined
      if (aid && typeof aid === 'object') {
        allAids.push({ aid, role: 'supportAids', idx: i })
      }
    }
  }

  for (const { aid, role, idx } of allAids) {
    const aidType = aid.type as string | undefined
    if (aidType && typeof aidType === 'string') {
      // Check unknown type
      if (!KNOWN_VISUAL_TYPES.has(aidType)) {
        issues.push(
          blocker(
            'visual',
            STAGE,
            'VISUAL_UNKNOWN_AID_TYPE',
            `Visual aid type "${aidType}" is not a known VisualAidType`,
            {
              field: `${role}[${idx}].type`,
              actual: aidType,
              questionId: qId,
              familyId: fId,
              suggestion: `Valid types: ${VISUAL_AID_TYPES.join(', ')}`,
            },
          ),
        )
      }

      // Check data schema — surfaces missing required fields (coach),
      // wrong primitive types, out-of-range numbers, and wrong array
      // item types (blockers). See validateVisualDataSchema doc-comment.
      const data = aid.data as Record<string, unknown> | undefined
      if (data && typeof data === 'object') {
        const violations = validateVisualDataSchema(aidType, data)
        for (const v of violations) {
          const meta = {
            field: `${role}[${idx}].data.${v.field}`,
            questionId: qId,
            familyId: fId,
            suggestion: v.suggestion,
          }
          if (v.severity === 'blocker') {
            issues.push(
              blocker(
                'visual',
                STAGE,
                'VISUAL_DATA_SCHEMA_VIOLATION',
                `Visual aid "${aidType}": ${v.message}`,
                meta,
              ),
            )
          } else {
            issues.push(
              coach(
                'visual',
                STAGE,
                'VISUAL_DATA_SCHEMA_VIOLATION',
                `Visual aid "${aidType}" is ${v.message}`,
                { ...meta, severity: SEVERITY.NEAR_REVIEW },
              ),
            )
          }
        }
      }
    }
  }

  // ── Required visual check ────────────────────────────────────────────────

  if (isRequired && visualPolicy) {
    // Question types whose visualRole === 'embedded' have a built-in visual
    // surface — skip the stimulus-required check for them.
    // SSOT: QUESTION_TYPE_VISUAL_ROLE in questionTypeRegistry.ts.
    const isExemptType = isQuestionTypeVisuallyEmbedded(question.questionType)

    // Stimulus required check
    if (visualPolicy.stimulusRequired && !isExemptType) {
      const hasStimulus =
        Array.isArray(question.stimulusAids) && question.stimulusAids.length > 0

      // Exception: ordering-type questions within stimulus-required families
      const isOrderingException =
        question.questionType === 'ordering' && !hasStimulus

      if (!hasStimulus && !isOrderingException) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_REQUIRED',
            `Family "${question.familyId}" requires a stimulus visual aid`,
            {
              field: 'stimulusAids',
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
              suggestion: visualPolicy.stimulusDefault
                ? `Add a "${visualPolicy.stimulusDefault}" visual to stimulusAids`
                : 'Add an appropriate visual to stimulusAids',
            },
          ),
        )
      } else if (
        hasStimulus &&
        visualPolicy.stimulusAllowed &&
        visualPolicy.stimulusAllowed.length > 0
      ) {
        // Check stimulus type against allowed list (multi-type support)
        const allowedSet = new Set<string>(visualPolicy.stimulusAllowed)
        for (let i = 0; i < question.stimulusAids.length; i++) {
          const aid = question.stimulusAids[i] as
            | Record<string, unknown>
            | undefined
          if (aid && typeof aid === 'object' && 'type' in aid) {
            const aidType = aid.type as string
            if (!allowedSet.has(aidType)) {
              issues.push(
                coach(
                  'visual',
                  STAGE,
                  'VISUAL_STIMULUS_TYPE_MISMATCH',
                  `Stimulus visual type "${aidType}" is not in the allowed types [${visualPolicy.stimulusAllowed.join(', ')}] for family "${question.familyId}"`,
                  {
                    field: `stimulusAids[${i}].type`,
                    expected: visualPolicy.stimulusAllowed.join(', '),
                    actual: aidType,
                    questionId: qId,
                    familyId: fId,
                    severity: SEVERITY.LOW,
                    suggestion: `Consider using one of [${visualPolicy.stimulusAllowed.join(', ')}], or this type may be intentional for this question`,
                  },
                ),
              )
            }
          }
        }
      }
    }

    // Support required check
    if (visualPolicy.supportRequired) {
      const hasSupport =
        Array.isArray(question.supportAids) && question.supportAids.length > 0
      if (!hasSupport) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_SUPPORT_RECOMMENDED',
            `Family "${question.familyId}" should have a support visual aid`,
            {
              field: 'supportAids',
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_REVIEW,
              suggestion: visualPolicy.supportDefault
                ? `Add a "${visualPolicy.supportDefault}" visual to supportAids`
                : 'Add an appropriate visual to supportAids',
            },
          ),
        )
      } else if (hasSupport) {
        // Check support type against allowed list (multi-type support)
        if (
          visualPolicy.supportAllowed &&
          visualPolicy.supportAllowed.length > 0
        ) {
          const allowedSet = new Set<string>(visualPolicy.supportAllowed)
          for (let i = 0; i < question.supportAids.length; i++) {
            const aid = question.supportAids[i] as
              | Record<string, unknown>
              | undefined
            if (aid && typeof aid === 'object' && 'type' in aid) {
              const aidType = aid.type as string
              if (!allowedSet.has(aidType)) {
                issues.push(
                  coach(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_TYPE_MISMATCH',
                    `Support visual type "${aidType}" is not in the allowed types [${visualPolicy.supportAllowed.join(', ')}] for family "${question.familyId}"`,
                    {
                      field: `supportAids[${i}].type`,
                      expected: visualPolicy.supportAllowed.join(', '),
                      actual: aidType,
                      questionId: qId,
                      familyId: fId,
                      severity: SEVERITY.NEAR_LOW,
                      suggestion: `Consider using one of [${visualPolicy.supportAllowed.join(', ')}], or this type may be intentional`,
                    },
                  ),
                )
              }
            }
          }
        }

        // showWhen validation
        if (visualPolicy.supportShowWhen) {
          for (let i = 0; i < question.supportAids.length; i++) {
            const aid = question.supportAids[i] as
              | Record<string, unknown>
              | undefined
            if (aid && typeof aid === 'object') {
              const showWhen = aid.showWhen as string | undefined
              if (!showWhen) {
                issues.push(
                  info(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_MISSING_SHOW_WHEN',
                    `Support aid at index ${i} is missing showWhen property — policy expects "${visualPolicy.supportShowWhen}"`,
                    {
                      field: `supportAids[${i}].showWhen`,
                      expected: visualPolicy.supportShowWhen,
                      questionId: qId,
                      familyId: fId,
                      severity: SEVERITY.NEAR_MINOR,
                      suggestion: `Add showWhen: "${visualPolicy.supportShowWhen}" to the support aid`,
                    },
                  ),
                )
              } else if (showWhen !== visualPolicy.supportShowWhen) {
                issues.push(
                  info(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_SHOW_WHEN_MISMATCH',
                    `Support aid showWhen "${showWhen}" does not match policy "${visualPolicy.supportShowWhen}" for family "${question.familyId}"`,
                    {
                      field: `supportAids[${i}].showWhen`,
                      expected: visualPolicy.supportShowWhen,
                      actual: showWhen,
                      questionId: qId,
                      familyId: fId,
                      severity: SEVERITY.COSMETIC,
                    },
                  ),
                )
              }
            }
          }
        }
      }
    }

    // ── Rule-only support check ──────────────────────────────────────────────
    //
    // RULEBOOK-AWARE (governance audit mei 2026): suppress this coach
    // signal when the matched rulebook hint explicitly declares
    // `preferredSupport: 'rule'` — in those families (e.g.
    // ^GET-WETENSCHAP) a rule-card IS the canonical support and
    // surfacing this nudge is a false positive that contradicts the
    // rulebook.

    if (
      Array.isArray(question.supportAids) &&
      question.supportAids.length > 0
    ) {
      const allRule = question.supportAids.every((aid) => {
        const aidObj = aid as Record<string, unknown> | undefined
        return aidObj && typeof aidObj === 'object' && aidObj.type === 'rule'
      })
      if (allRule) {
        // STIMULUS-AWARE SKIP (juni 2026): if the stimulus already
        // contains a non-rule didactic visual (e.g. textStructureSchema,
        // readingPassage, fractionStrip), the question is NOT
        // didactically text-only — the scaffolding lives in the stimulus.
        // Surfacing this nudge then contradicts the seed's intent
        // (initially observed on TAA-LEZEN-STRUCTUUR-007 where the
        // textStructureSchema IS the analytic artefact).
        const stimulusHasDidacticVisual =
          Array.isArray(question.stimulusAids) &&
          question.stimulusAids.some((aid) => {
            const aidObj = aid as Record<string, unknown> | undefined
            const t =
              aidObj && typeof aidObj === 'object'
                ? (aidObj.type as string | undefined)
                : undefined
            return typeof t === 'string' && t !== 'rule'
          })

        // Check rulebook override — is 'rule' the canonical support for this family?
        // RULEBOOK-AWARE (governance refactor mei 2026): also accept
        // `roleScope === 'rule-only'` as a canonical-rule declaration.
        const rulebook = getModuleRulebook(question.module)
        const hints = rulebook?.visuals?.familyVisualHints ?? []
        let ruleIsCanonical = false
        for (const hint of hints) {
          try {
            if (new RegExp(hint.familyIdPattern).test(fId)) {
              if (
                hint.preferredSupport === 'rule' ||
                hint.roleScope === 'rule-only'
              ) {
                ruleIsCanonical = true
              }
              // First matching hint wins (same convention as elsewhere).
              break
            }
          } catch {
            continue
          }
        }
        if (!ruleIsCanonical && !stimulusHasDidacticVisual) {
          issues.push(
            coach(
              'visual',
              STAGE,
              'VISUAL_SUPPORT_RULE_ONLY',
              `Question has only "rule" as support — consider adding a didactic visual`,
              {
                field: 'supportAids',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.LOW,
                suggestion:
                  'A "rule" is text-only support. Consider adding a visual like numberline, fraction, stairs, etc. for better scaffolding.',
              },
            ),
          )
        }
      }
    }

    // ── Rulebook familyVisualHint compliance ─────────────────────────────────
    // Pass the policy allowed-lists so the hint check can skip emissions
    // that VISUAL_STIMULUS_TYPE_MISMATCH / VISUAL_SUPPORT_TYPE_MISMATCH
    // already cover (shrink cleanup).
    const stimulusAllowedSet =
      visualPolicy.stimulusAllowed && visualPolicy.stimulusAllowed.length > 0
        ? new Set<string>(visualPolicy.stimulusAllowed)
        : undefined
    const supportAllowedSet =
      visualPolicy.supportAllowed && visualPolicy.supportAllowed.length > 0
        ? new Set<string>(visualPolicy.supportAllowed)
        : undefined
    issues.push(
      ...runRulebookHintCheck(question, stimulusAllowedSet, supportAllowedSet),
    )
  }

  // ── Semantic convention check ────────────────────────────────────────────

  if (
    question.stimulusAids !== undefined &&
    !Array.isArray(question.stimulusAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_STIMULUS_INVALID_TYPE',
        'stimulusAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'stimulusAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    question.supportAids !== undefined &&
    !Array.isArray(question.supportAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_SUPPORT_INVALID_TYPE',
        'supportAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'supportAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // ── Answer leakage check ─────────────────────────────────────────────────

  if (
    Array.isArray(question.stimulusAids) &&
    question.stimulusAids.length > 0
  ) {
    // Collect candidate "correct answer" strings from MC + fill_in
    const candidateAnswers: string[] = []

    if (
      question.questionType === 'multiple_choice' &&
      Array.isArray(question.options)
    ) {
      const mcAns = question.options[question.correctAnswer ?? -1]
      if (typeof mcAns === 'string' && mcAns.trim()) {
        candidateAnswers.push(mcAns.trim())
      }
    }

    // fill_in: data.acceptedAnswers or top-level correctAnswer string
    const qData = (question as unknown as { data?: Record<string, unknown> })
      .data
    if (qData && Array.isArray(qData.acceptedAnswers)) {
      for (const a of qData.acceptedAnswers) {
        if (typeof a === 'string' && a.trim()) candidateAnswers.push(a.trim())
      }
    }
    if (
      typeof question.correctAnswer === 'string' &&
      (question.correctAnswer as string).trim()
    ) {
      candidateAnswers.push((question.correctAnswer as string).trim())
    }

    // Build numeric variants too (e.g. "2,5" ↔ 2.5)
    const numericAnswers = new Set<number>()
    for (const a of candidateAnswers) {
      const normalized = a.replace(',', '.').replace(/[^0-9.\-]/g, '')
      if (normalized) {
        const n = Number(normalized)
        if (Number.isFinite(n)) numericAnswers.add(n)
      }
    }

    for (let i = 0; i < question.stimulusAids.length; i++) {
      const aid = question.stimulusAids[i]
      if (!aid || typeof aid !== 'object' || !('data' in aid)) continue
      const data = (aid as { data?: Record<string, unknown> }).data
      if (!data || typeof data !== 'object') continue

      const dataStr = JSON.stringify(data).toLowerCase()
      let leaked = false
      let reason = ''

      // 1) JSON string match on label/value
      for (const ans of candidateAnswers) {
        const answerLower = ans.toLowerCase()
        if (
          dataStr.includes(`"label":"${answerLower}"`) ||
          dataStr.includes(`"value":"${answerLower}"`)
        ) {
          leaked = true
          reason = `leaks "${ans}" via label/value`
          break
        }
      }

      // 2) Numeric leakage via marked/highlight arrays
      if (!leaked && numericAnswers.size > 0) {
        const numericFields = ['marked', 'highlight', 'highlightValues']
        for (const field of numericFields) {
          const arr = (data as Record<string, unknown>)[field]
          if (!Array.isArray(arr)) continue
          for (const v of arr) {
            const n = typeof v === 'number' ? v : Number(v)
            if (Number.isFinite(n) && numericAnswers.has(n)) {
              leaked = true
              reason = `leaks ${n} via stimulus.${field}`
              break
            }
          }
          if (leaked) break
        }
      }

      // 3) Numeric leakage via fractionLabels (array of strings like "1/4")
      if (!leaked) {
        const fLabels = (data as Record<string, unknown>).fractionLabels
        if (Array.isArray(fLabels)) {
          for (const lbl of fLabels) {
            if (typeof lbl !== 'string') continue
            const lblLower = lbl.toLowerCase().trim()
            for (const ans of candidateAnswers) {
              if (lblLower === ans.toLowerCase().trim()) {
                leaked = true
                reason = `leaks "${ans}" via stimulus.fractionLabels`
                break
              }
            }
            if (leaked) break
          }
        }
      }

      if (leaked) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_ANSWER_LEAKAGE',
            `Stimulus visual at index ${i} ${reason}`,
            {
              field: `stimulusAids[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_HIGH,
              suggestion:
                'Remove the leaking value from the stimulus, or move this visual to supportAids with showWhen: "wrong".',
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Batch: Stimulus Consistency per Family ─────────────────────────────────

export function runVisualConsistencyBatch(
  questions: Question[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Group questions by familyId
  const familyMap = new Map<string, Question[]>()
  for (const q of questions) {
    if (!q.familyId) continue
    const list = familyMap.get(q.familyId) ?? []
    list.push(q)
    familyMap.set(q.familyId, list)
  }

  for (const [familyId, familyQuestions] of familyMap) {
    // Collect stimulus types per question (only questions that HAVE stimulus aids)
    const typeCountMap = new Map<string, number>()
    const questionsByType = new Map<string, string[]>()

    for (const q of familyQuestions) {
      if (!Array.isArray(q.stimulusAids) || q.stimulusAids.length === 0)
        continue

      for (const aid of q.stimulusAids) {
        const aidObj = aid as Record<string, unknown> | undefined
        if (
          aidObj &&
          typeof aidObj === 'object' &&
          typeof aidObj.type === 'string'
        ) {
          const t = aidObj.type as string
          typeCountMap.set(t, (typeCountMap.get(t) ?? 0) + 1)
          const ids = questionsByType.get(t) ?? []
          ids.push(q.id ?? 'UNKNOWN')
          questionsByType.set(t, ids)
        }
      }
    }

    // If 0 or 1 distinct types → consistent, skip
    if (typeCountMap.size <= 1) continue

    // RULEBOOK-AWARE (governance refactor mei 2026): if the matching
    // family-visual-hint declares `stimulusAllowed`, and ALL observed
    // stimulus types are inside that whitelist, the mix is intentional
    // and the check should be silent. Example: ^GET-NEGATIEF legitimately
    // uses both `numberline` and `thermometer`.
    const moduleIdForFamily = familyQuestions.find((q) => q.module)?.module
    const rulebookForFamily = moduleIdForFamily
      ? getModuleRulebook(moduleIdForFamily)
      : null
    const familyHints = rulebookForFamily?.visuals?.familyVisualHints ?? []
    let allowedSet: Set<string> | null = null
    for (const hint of familyHints) {
      try {
        if (new RegExp(hint.familyIdPattern).test(familyId)) {
          if (
            Array.isArray(hint.stimulusAllowed) &&
            hint.stimulusAllowed.length > 0
          ) {
            allowedSet = new Set<string>(hint.stimulusAllowed)
          }
          break // first match wins (top-down convention)
        }
      } catch {
        continue
      }
    }
    if (allowedSet) {
      let allInsideWhitelist = true
      for (const t of typeCountMap.keys()) {
        if (!allowedSet.has(t)) {
          allInsideWhitelist = false
          break
        }
      }
      if (allInsideWhitelist) continue // intentional mix per rulebook
    }

    // Find majority type
    let majorityType = ''
    let majorityCount = 0
    for (const [t, count] of typeCountMap) {
      if (count > majorityCount) {
        majorityType = t
        majorityCount = count
      }
    }

    // Flag minority types
    for (const [t, qIds] of questionsByType) {
      if (t === majorityType) continue
      for (const qId of qIds) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY',
            `Family "${familyId}" mixes stimulus types: "${t}" (${qIds.length}×) vs majority "${majorityType}" (${majorityCount}×)`,
            {
              field: 'stimulusAids',
              expected: majorityType,
              actual: t,
              questionId: qId,
              familyId,
              severity: SEVERITY.NEAR_MEDIUM,
              suggestion: `Align stimulus type to "${majorityType}" for consistency, or remove the visual if it doesn't add value`,
            },
          ),
        )
      }
    }
  }

  // ── Family-level rulebook-hint drift check ───────────────────────────────
  for (const [familyId, familyQuestions] of familyMap) {
    const moduleId = familyQuestions.find((q) => q.module)?.module
    if (!moduleId) continue
    const rulebook = getModuleRulebook(moduleId)
    if (!rulebook) continue
    const hints = rulebook.visuals?.familyVisualHints
    if (!hints || hints.length === 0) continue

    // Find the first hint that matches this family — patterns are top-down
    let matchedHint: (typeof hints)[number] | undefined
    for (const hint of hints) {
      try {
        if (new RegExp(hint.familyIdPattern).test(familyId)) {
          matchedHint = hint
          break
        }
      } catch {
        continue
      }
    }
    if (!matchedHint) continue

    // — Stimulus aggregation —
    if (matchedHint.preferredStimulus) {
      let withStimulus = 0
      let disagreeing = 0
      let firstDeviatingQId = ''
      const exemptTypes = deriveQuestionTypeExempt(matchedHint)
      const stimulusAllowed = new Set<string>([
        matchedHint.preferredStimulus,
        ...(matchedHint.stimulusAllowed ?? []),
      ])
      for (const q of familyQuestions) {
        if (exemptTypes.has(q.questionType ?? '')) continue
        if (!Array.isArray(q.stimulusAids) || q.stimulusAids.length === 0)
          continue
        withStimulus++
        const firstAid = q.stimulusAids[0] as
          | Record<string, unknown>
          | undefined
        const aidType =
          firstAid &&
          typeof firstAid === 'object' &&
          typeof firstAid.type === 'string'
            ? (firstAid.type as string)
            : undefined
        if (aidType && !stimulusAllowed.has(aidType)) {
          disagreeing++
          if (!firstDeviatingQId) firstDeviatingQId = q.id ?? ''
        }
      }
      if (withStimulus >= 3 && disagreeing / withStimulus >= 0.8) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_RULEBOOK_HINT_FAMILY_DRIFT',
            `Family "${familyId}" mass-drifts from rulebook stimulus hint "${matchedHint.preferredStimulus}" (${disagreeing}/${withStimulus} questions disagree)`,
            {
              field: 'stimulusAids',
              expected: matchedHint.preferredStimulus,
              questionId: firstDeviatingQId || (familyQuestions[0]?.id ?? ''),
              familyId,
              severity: SEVERITY.LOW,
              suggestion: matchedHint.note
                ? `Either migrate the whole family to "${matchedHint.preferredStimulus}" (rulebook: ${matchedHint.note}) or relax the rulebook hint if the seeds reflect intentional pedagogy.`
                : `Either migrate the whole family to "${matchedHint.preferredStimulus}" or relax the rulebook hint.`,
            },
          ),
        )
      }
    }

    // — Support aggregation —
    if (matchedHint.preferredSupport) {
      let withSupport = 0
      let disagreeing = 0
      let firstDeviatingQId = ''
      const exemptTypes = deriveQuestionTypeExempt(matchedHint)
      const supportAllowed = new Set<string>([
        matchedHint.preferredSupport,
        ...(matchedHint.supportAllowed ?? []),
      ])
      for (const q of familyQuestions) {
        if (exemptTypes.has(q.questionType ?? '')) continue
        if (!Array.isArray(q.supportAids) || q.supportAids.length === 0)
          continue
        withSupport++
        const firstAid = q.supportAids[0] as Record<string, unknown> | undefined
        const aidType =
          firstAid &&
          typeof firstAid === 'object' &&
          typeof firstAid.type === 'string'
            ? (firstAid.type as string)
            : undefined
        if (aidType && !supportAllowed.has(aidType)) {
          disagreeing++
          if (!firstDeviatingQId) firstDeviatingQId = q.id ?? ''
        }
      }
      if (withSupport >= 3 && disagreeing / withSupport >= 0.8) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_RULEBOOK_HINT_FAMILY_DRIFT',
            `Family "${familyId}" mass-drifts from rulebook support hint "${matchedHint.preferredSupport}" (${disagreeing}/${withSupport} questions disagree)`,
            {
              field: 'supportAids',
              expected: matchedHint.preferredSupport,
              questionId: firstDeviatingQId || (familyQuestions[0]?.id ?? ''),
              familyId,
              severity: SEVERITY.LOW,
              suggestion: matchedHint.note
                ? `Either migrate supports to "${matchedHint.preferredSupport}" (rulebook: ${matchedHint.note}) or relax the rulebook hint.`
                : `Either migrate supports to "${matchedHint.preferredSupport}" or relax the rulebook hint.`,
            },
          ),
        )
      }
    }
  }

  return issues
}
